import { Box, Flex, Text } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { useIntegrationSyncPolling } from 'hooks/common/useIntegrationSyncPolling';
import { useSyncXero } from 'hooks/integrations/useSyncXero';
import { fetchPaginatedXeroProjectTasks } from 'hooks/integrations/xero/useFetchPaginatedXeroProjectTasks';
import {
  IXeroTimeeroTasksSearchQuery,
  useFetchPaginatedXeroTimeeroTasks,
} from 'hooks/integrations/xero/useFetchPaginatedXeroTimeeroTasks';
import {
  IMapUnmapXeroProjectTasks,
  useMapUnmapXeroProjectTasks,
} from 'hooks/integrations/xero/useMapUnmapXeroProjectTasks';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import EmployeeMappingTableFooter from './TableFooter';

const initialSearchQuery: IXeroTimeeroTasksSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const ProjectTasksMapping = () => {
  const [mappedTasks, setMappedTasks] = useState<any>(null);
  const [currentMapTask, setCurrentMapTask] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IXeroTimeeroTasksSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['common', 'integrations']);

  const { data: timeeroTasksData, status: timeeroTaskStatus, refetch } = useFetchPaginatedXeroTimeeroTasks(searchQuery);

  const { status: mapUnmapTasksStatus, mutateAsync: mutateAsyncMapUnmapTasks } = useMapUnmapXeroProjectTasks();
  const {
    status: xeroSyncStatus,
    mutateAsync: xeroSyncMutateAsync,
    data: syncData,
    isError: isSyncError,
  } = useSyncXero();

  const { syncLongPollingStatus } = useIntegrationSyncPolling({ id: syncData?.payload?.id, refetch: refetch });

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    {
      Header: t('integrations:timeeroTasks'),
      accessor: 'name',
    },
    {
      Header: t('integrations:sandataIndiana.taskStatus'),
      accessor: 'task_status',
      Cell: (data: CellValue) => {
        return data.value ? (
          <Text color="success.500" fontWeight="semibold">
            {t('common:active')}
          </Text>
        ) : (
          <Text color="secondary.700" fontWeight="semibold">
            {t('common:inactive')}
          </Text>
        );
      },
    },
    {
      Header: t('integrations:xero.xeroProjectTasks'),
      accessor: 'list',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={timeeroEmployeeLoadOptions}
            isDisabled={mapUnmapTasksStatus === 'loading' && currentMapTask === data.row.original.id}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.xero_task
                ? data.row.original?.xero_task || null
                : mappedTasks &&
                    mappedTasks[`${data.row.original.id}`] &&
                    mappedTasks[`${data.row.original.id}`].xero_task
                  ? mappedTasks[`${data.row.original.id}`].xero_task
                  : null
            }
            onChange={(value: any) => {
              setMappedTasks((originalValues: any) => {
                const prevValues = originalValues ? { ...originalValues } : null;

                if (prevValues) {
                  const oldValue = Object.entries(prevValues).find(([_, prevValue]: any) => {
                    return prevValue?.xero_task?.id === value.id;
                  });

                  if (oldValue) {
                    delete prevValues[oldValue[0]];
                  }
                }

                return {
                  ...prevValues,
                  [data.row.original.id]: {
                    xero_task: value || null,
                  },
                };
              });
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('integrations:selectTask')}
            getOptionLabel={(option: any) => option.name}
            getOptionValue={(option: any) => option.id}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 170,
      Cell: (data: CellValue) => {
        return data.row.original?.xero_task ? (
          <Button
            _hover={{ bg: 'secondary.100' }}
            fontWeight="bold"
            background="rgba(250, 85, 85, 0.2)"
            color="rgba(250, 85, 85, 1)"
            isLoading={mapUnmapTasksStatus === 'loading' && currentMapTask === data.row.original.id}
            onClick={() => onUnMapTasks(data.row.original)}
          >
            {t('integrations:removeMapping')}
          </Button>
        ) : (
          <Button
            _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
            background="rgba(255, 103, 32, 0.1)"
            colorScheme="rgba(255, 103, 32, 0.3)"
            color="secondary.500"
            isLoading={mapUnmapTasksStatus === 'loading' && currentMapTask === data.row.original.id}
            onClick={() => onMapTasks(data.row.original.id)}
          >
            {t('integrations:mapTask')}
          </Button>
        );
      },
    },
  ];

  const onSyncGusto = () => {
    xeroSyncMutateAsync();
    syncLongPollingStatus.current = 'fetching';
  };

  const timeeroEmployeeLoadOptions = async (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedXeroProjectTasks, { search, page });
  };

  const onMapTasks = (xero_taskId: number) => {
    if (mappedTasks && mappedTasks[xero_taskId] && mappedTasks[xero_taskId].hasOwnProperty('xero_task')) {
      setCurrentMapTask(xero_taskId);

      const postData: IMapUnmapXeroProjectTasks = {
        mapping: true,
        data: [
          {
            timeero_task_id: xero_taskId,
            xero_task_id: mappedTasks[xero_taskId].xero_task.id,
          },
        ],
      };

      mutateAsyncMapUnmapTasks(postData).then(() => {
        refetch();
        setCurrentMapTask(null);
      });
    }
  };

  const onUnMapTasks = (data: any) => {
    setCurrentMapTask(data.id);
    const postData: IMapUnmapXeroProjectTasks = {
      mapping: false,
      data: [
        {
          timeero_task_id: data.id,
          xero_task_id: data?.xero_task?.id,
        },
      ],
    };

    mutateAsyncMapUnmapTasks(postData).then(() => {
      refetch();
      setCurrentMapTask(null);
      setMappedTasks(null);
    });
  };

  const handleBulkMapUnmapTasks = (selectedRows: Array<any>, type: 'map' | 'unmap') => {
    const mappedData: Array<{ timeero_task_id: number; xero_task_id: number }> = [];

    if (type === 'map') {
      selectedRows.forEach(selectedRow => {
        if (mappedTasks && mappedTasks[selectedRow.original.id]) {
          mappedData.push({
            xero_task_id: mappedTasks[selectedRow.original.id]?.xero_task?.id,
            timeero_task_id: selectedRow?.original?.id,
          });
        }
      });

      setCurrentMapTask('bulkMap');
    } else {
      selectedRows.forEach(selectedRow => {
        if (selectedRow?.original?.xero_task) {
          mappedData.push({
            xero_task_id: selectedRow?.original?.xero_task?.id,
            timeero_task_id: selectedRow?.original?.id,
          });
        }
      });
      setCurrentMapTask('bulkUnmap');
    }

    const postData: IMapUnmapXeroProjectTasks = {
      mapping: type === 'map' ? true : false,
      data: mappedData,
    };

    mutateAsyncMapUnmapTasks(postData).then(() => {
      refetch();
      setCurrentMapTask(null);
      setMappedTasks(null);
    });
  };

  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IXeroTimeeroTasksSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    const updatedSearchQuery = (previousSearchQuery: IXeroTimeeroTasksSearchQuery) => ({
      ...previousSearchQuery,
      page_size: pageSize,
      page: 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:xero.xeroMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={!isSyncError && (xeroSyncStatus === 'loading' || syncLongPollingStatus.current === 'fetching')}
          onClick={onSyncGusto}
        >
          {t('integrations:xero.syncOnline')}
        </Button>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: 'Search Tasks',
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <Table
        columns={columns}
        data={timeeroTasksData?.payload || []}
        isLoading={timeeroTaskStatus === 'loading'}
        enableRowSelect
        paginationData={timeeroTasksData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <EmployeeMappingTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              mappedTasks={mappedTasks}
              isBulkMapLoading={currentMapTask === 'bulkMap' && mapUnmapTasksStatus === 'loading'}
              isBulkUnMapLoading={currentMapTask === 'bulkUnmap' && mapUnmapTasksStatus === 'loading'}
              handleBulkMapUnmapTasks={handleBulkMapUnmapTasks}
            />
          );
        }}
      />
    </Box>
  );
};
export default ProjectTasksMapping;
